import { defineStore } from 'pinia';
import { forEach } from 'lodash-es';
import chain from '@/lib/utils/chain';
import { MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX } from '@/lib/constants/manuscripts/api';
import {
  GENERATE_STATUS_NOT_GENERATED,
  GENERATE_STATUS_GENERATING,
  GENERATE_STATUS_GENERATED,
  GENERATE_STATUS_REGENERATE_NEEDED,
  GENERATE_STATUS_DISABLED,
  GENERATE_STATUS_ERROR,
  GENERATE_STATUS_MODIFIED,
  QUEUE_ITEM_STATUS_QUEUED,
  QUEUE_ITEM_STATUS_PROCESSING,
} from '@/lib/constants/manuscripts/store';
import * as action from '@/lib/constants/manuscripts/store';
import { USER_COLORS } from '@/lib/constants';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { useUserStore } from '@/stores';

const defaultSearchParams = {
  // Default search params
  sort_field: 'created_on',
  sort_direction: 'desc',
  page: 1,
  per_page: 25,
  assignee_id: [],
  project_id: [],
  progress_status_id: [],
  manuscript_id: [],
  unassigned: false,
  // More Filters: date range (empty string = no bound on this side; stripped from URL by searchParamsOptions)
  date_from: '',
  date_to: '',
  // More Filters: multi-select arrays (empty array = no filter; stripped from URL by searchParamsOptions)
  type_id: [],
  grade: [],
  reading_level: [],
  language: [],
};

const useManuscriptStore = defineStore(
  'manuscript-manuscript',

  {
    state: () => ({
      assigneesList: [],
      confirmDeleteManuscriptDialog: null,
      confirmNavigateDialog: null,
      confirmTabNavigationDialog: null,
      hasUserSetPerPage: false,
      isProcessingRequest: false,
      manuscript: null,
      manuscriptEditorIsFullscreen: false,
      manuscripts: [],
      metadataHasUnsavedChanges: false,
      searchText: '',
      searchParams: {
        ...defaultSearchParams,
      },
      selectedManuscriptIds: [],
      statusCodes: [],
      lessonTypes: [],
      pagination: {},
      productConfigs: null,
      usersAssignedAColor: {},
      generateLessonState: {},
      bulkGenerateLessonJobs: [],
    }),

    getters: {
      selectedManuscripts() {
        return this.selectedManuscriptIds.reduce((manuscripts, id) => {
          const manuscript = this.manuscripts.find((m) => m.manuscript_id === id);

          if (manuscript) manuscripts.push(manuscript);

          return manuscripts;
        }, []);
      },
      lastGeneration() {
        const validGeneration = (item) => item.generated_on
          && item.entity === 'lesson';

        return (manuscript) => {
          const lastGeneration = chain(manuscript.generation_meta?.history)
            .filter(validGeneration)
            .sortBy((item) => new Date(item.generated_on))
            .last()
            .value();

          return lastGeneration;
        };
      },
      generateLessonStatus() {
        return (manuscript) => {
          if (
            !manuscript
            || !manuscript.product_id
            || !manuscript.domain_id
            || !manuscript.grade_ids?.length
          ) {
            return GENERATE_STATUS_DISABLED;
          }

          const manuscriptId = manuscript.manuscript_id;
          const singleGenerateStatus = this.generateLessonState[manuscriptId]?.status;

          let isGenerating = singleGenerateStatus === GENERATE_STATUS_GENERATING;

          if (!isGenerating) {
            isGenerating = this.bulkGenerateLessonJobs.some(
              (job) => job.manuscriptIds.includes(manuscriptId),
            );
          }

          if (isGenerating) {
            return GENERATE_STATUS_GENERATING;
          }

          if (singleGenerateStatus === GENERATE_STATUS_ERROR) {
            return GENERATE_STATUS_ERROR;
          }

          const lastGeneration = this.lastGeneration(manuscript);

          if (lastGeneration) {
            let {
              generated_on: generatedOn,
            } = lastGeneration;
            let updatedOn = manuscript?.metadata?.updated_on;

            if (generatedOn) {
              generatedOn = new Date(generatedOn);
            }

            if (updatedOn) {
              updatedOn = new Date(updatedOn);
            }

            if (
              generatedOn
              && updatedOn
              && generatedOn < updatedOn
            ) {
              return GENERATE_STATUS_REGENERATE_NEEDED;
            }

            const hasError = !!lastGeneration.error
              || !lastGeneration.generated_lesson_id;

            if (hasError) {
              return GENERATE_STATUS_ERROR;
            }
            const hasModifiedSinceGeneration = manuscript.modified_since_generation === true
              || manuscript.generation_meta?.modified_since_generation === true
              || manuscript.pages?.some((page) => {
                if (page.modified_since_generation === true) return true;
                if (page.student_content?.some((c) => c.modified_since_generation === true)) return true;
                if (page.teacher_content?.some((c) => c.modified_since_generation === true)) return true;
                return false;
              });

            if (hasModifiedSinceGeneration) {
              return GENERATE_STATUS_MODIFIED;
            }
            return GENERATE_STATUS_GENERATED;
          }

          return GENERATE_STATUS_NOT_GENERATED;
        };
      },
      searchParamsOptions(state) {
        // Filter out empty values except for pagination and sorting params
        const paramsWithValue = Object.fromEntries(
          Object.entries({ ...state.searchParams, search_text: state.searchText })
            .filter(([key, value]) => (key.length && (Boolean(value?.length) || (typeof value === 'boolean' && !!value)))
              || ['page', 'per_page', 'sort_field', 'sort_direction'].includes(key)),
        );
        // Then flatten array values for URLSearchParams
        const flatParams = Object.entries(paramsWithValue).flatMap(
          ([key, value]) => {
            if (Array.isArray(value)) {
              return value.map((v) => [key, v]);
            }
            return [[key, value]];
          });
        // Then set array for key/value pairs
        const searchParams = new URLSearchParams(flatParams);
        return {
          string: searchParams.toString(),
          object: paramsWithValue,
        };
      },
      checkAllCheckboxIsChecked(state) {
        if (!state.manuscripts.length) return false;
        const currentPageManuscriptIds = state.manuscripts.map((manuscript) => manuscript.manuscript_id);
        // If maximum selectable manuscripts is reached.
        if (state.selectedManuscriptIds.length === action.MAXIMUM_SELECTABLE_MANUSCRIPTS) {
          return currentPageManuscriptIds.some((manuscriptId) => this.selectedManuscriptIds.includes(manuscriptId));
        }

        // If max is not reached AND per_page is <= maximum:
        if (state.searchParams.per_page < action.MAXIMUM_SELECTABLE_MANUSCRIPTS) {
          // Check current page has all manuscripts in selectedManuscriptIds, return true else false
          return currentPageManuscriptIds.every((manuscriptId) => this.selectedManuscriptIds.includes(manuscriptId));
        }

        // If max is NOT reached AND per_page is > maximum, check-all should only be checked if all
        // currentPageManuscriptIds are in the selectedManuscriptIds
        return false;
      },
      hasFiltersApplied({ searchParams }) {
        const nonFilterParams = [
          'page',
          'per_page',
          'sort_field',
          'sort_direction',
          'manuscript_id',
        ];

        return Object.entries(searchParams).some(
          ([key, value]) => !nonFilterParams.includes(key)
              && (typeof value === 'boolean' ? value : !!value?.length),
        );
      },
      getProductStatusList({ productConfigs }) {
        return (productId) => {
          const product = productConfigs?.find((config) => config.product_id === productId);
          return product ? product.progress_statuses : [];
        };
      },
    },

    actions: {
      [action.SET_CURRENT_PAGE](page) {
        this.searchParams.page = page;
      },

      [action.SET_MANUSCRIPT_EDITOR_IS_FULLSCREEN](value) {
        this.manuscriptEditorIsFullscreen = value;
      },

      [action.SET_FILTER_BY_SEARCH_TEXT](searchText) {
        this.searchText = searchText;
      },

      [action.SET_FILTER_BY_ASSIGNEE_IDS](assigneeIds) {
        this.searchParams.page = 1; // Reset to first page when changing per_page
        this.searchParams.assignee_id = assigneeIds;
      },

      [action.SET_FILTER_BY_UNASSIGNED](value) {
        this.searchParams.page = 1; // Reset to first page when changing per_page
        this.searchParams.unassigned = value;
      },

      [action.SET_FILTER_BY_PROJECT_IDS](projectIds) {
        this.searchParams.page = 1; // Reset to first page when changing per_page
        this.searchParams.project_id = projectIds;
      },

      [action.SET_FILTER_BY_STATUSES](statuses) {
        this.searchParams.page = 1; // Reset to first page when changing per_page
        this.searchParams.progress_status_id = statuses;
      },

      /**
       * Set the date range filter.
       * Empty strings mean "no bound on this side"; both are normalized to ''
       * so that searchParamsOptions's !!value?.length check strips them from
       * the URL when unset, keeping the URL clean.
       * date_from and date_to are inclusive — the BE treats date_to as
       * end-of-day (datetime.time.max). See OPEN QUESTION 2 in spec.md.
       */
      // eslint-disable-next-line camelcase
      [action.SET_FILTER_BY_DATE_RANGE]({ date_from: dateFrom, date_to: dateTo }) {
        this.searchParams.page = 1;
        this.searchParams.date_from = dateFrom || '';
        this.searchParams.date_to = dateTo || '';
      },

      [action.SET_FILTER_BY_LESSON_TYPES](typeIds) {
        this.searchParams.page = 1;
        this.searchParams.type_id = typeIds;
      },

      [action.SET_FILTER_BY_GRADES](gradeIds) {
        this.searchParams.page = 1;
        this.searchParams.grade = gradeIds;
      },

      [action.SET_FILTER_BY_READING_LEVELS](levels) {
        this.searchParams.page = 1;
        this.searchParams.reading_level = levels;
      },

      [action.SET_FILTER_BY_LANGUAGES](languages) {
        this.searchParams.page = 1;
        this.searchParams.language = languages;
      },

      [action.SET_SORT_BY](sortBy) {
        this.searchParams.sort_field = sortBy;
      },

      [action.SET_SORT_DIRECTION](sortDirection) {
        this.searchParams.sort_direction = sortDirection;
      },

      [action.SET_MANUSCRIPTS_PER_PAGE](perPage) {
        this.searchParams.page = 1; // Reset to first page when changing per_page
        this.searchParams.per_page = perPage;
      },

      [action.SET_HAS_USER_SET_PER_PAGE](hasUserSetPerPage) {
        this.hasUserSetPerPage = hasUserSetPerPage;
      },

      [action.RESET_ALL_FILTERS]() {
        this.searchText = '';
        this.searchParams = {
          ...defaultSearchParams,
        };
        this.hasUserSetPerPage = false;
      },

      [action.SET_CONFIRM_DELETE_MANUSCRIPT_DIALOG](dialogState) {
        this.confirmDeleteManuscriptDialog = dialogState;
      },

      [action.SET_CONFIRM_TAB_NAVIGATION_DIALOG](dialogState) {
        this.confirmTabNavigationDialog = dialogState;
      },

      [action.SET_CONFIRM_NAVIGATE_DIALOG](dialogState) {
        this.confirmNavigateDialog = dialogState;
      },

      [action.SET_METADATA_HAS_UNSAVED_CHANGES](hasChanges) {
        this.metadataHasUnsavedChanges = hasChanges;
      },

      [action.SET_INITIAL_SEARCH_PARAMS](query) {
        const {
          page,
          per_page: perPage,
          sort_field: sortBy,
          sort_direction: sortDirection,
          search_text: searchText,
          assignee_id: assigneeIds,
          project_id: projectIds,
          progress_status_id: status,
          manuscript_id: manuscriptIds,
          date_from: dateFrom,
          date_to: dateTo,
          type_id: typeIds,
          grade: gradeIds,
          reading_level: readingLevels,
          language: languages,
          topHatType,
        } = query;

        // A Top Hat entry can declare which list filters it wants reset
        // to module defaults via its `resetsFilters` array. Filters not
        // listed are preserved from the current store state. Unknown
        // topHatType -> no reset. manuscript_id is NEVER auto-reset
        // here -- it's the intentional URL filter.
        const entry = topHatType ? action.TOP_HAT_MESSAGES.get(topHatType) : null;
        const resetsFilters = new Set(entry?.resetsFilters || []);

        const pageDefault = resetsFilters.has('page') ? defaultSearchParams.page : this.searchParams.page;
        const perPageDefault = resetsFilters.has('per_page') ? defaultSearchParams.per_page : this.searchParams.per_page;
        const sortFieldDefault = resetsFilters.has('sort_field') ? defaultSearchParams.sort_field : this.searchParams.sort_field;
        const sortDirectionDefault = resetsFilters.has('sort_direction') ? defaultSearchParams.sort_direction : this.searchParams.sort_direction;

        const assigneeDefault = resetsFilters.has('assignee_id') ? { assignee_id: [] } : {};
        const projectDefault = resetsFilters.has('project_id') ? { project_id: [] } : {};
        const statusDefault = resetsFilters.has('progress_status_id') ? { progress_status_id: [] } : {};
        const searchTextDefault = resetsFilters.has('searchText') ? { searchText: '' } : {};
        const manuscriptsDefault = { manuscript_id: [] };

        const assignees = assigneeIds?.length
          ? { assignee_id: Array.isArray(assigneeIds) ? assigneeIds : [assigneeIds] }
          : assigneeDefault;

        const projects = projectIds?.length
          ? { project_id: Array.isArray(projectIds) ? projectIds : [projectIds] }
          : projectDefault;

        const statuses = status?.length
          ? { progress_status_id: Array.isArray(status) ? status.map(Number) : [Number(status)] }
          : statusDefault;

        const manuscripts = manuscriptIds?.length
          ? { manuscript_id: Array.isArray(manuscriptIds) ? manuscriptIds : [manuscriptIds] }
          : manuscriptsDefault;

        const search = searchText?.length ? { searchText } : searchTextDefault;

        // Date range: preserve whichever bound is provided; default the other per resetsFilters.
        const dateFromDefault = resetsFilters.has('date_from') ? { date_from: '' } : {};
        const dateToDefault = resetsFilters.has('date_to') ? { date_to: '' } : {};
        const dateRange = (dateFrom || dateTo)
          ? { date_from: dateFrom || '', date_to: dateTo || '' }
          : { ...dateFromDefault, ...dateToDefault };

        // Lesson type IDs: array of UUIDs, no coercion needed.
        let types;
        if (typeIds?.length) {
          types = { type_id: Array.isArray(typeIds) ? typeIds : [typeIds] };
        } else {
          types = resetsFilters.has('type_id') ? { type_id: [] } : {};
        }

        // Grade IDs: array of UUIDs, no coercion needed.
        let grades;
        if (gradeIds?.length) {
          grades = { grade: Array.isArray(gradeIds) ? gradeIds : [gradeIds] };
        } else {
          grades = resetsFilters.has('grade') ? { grade: [] } : {};
        }

        // Reading levels: integers in the store, strings in the URL — coerce on read.
        let readings;
        if (readingLevels?.length) {
          const levelsArray = Array.isArray(readingLevels) ? readingLevels : [readingLevels];
          readings = { reading_level: levelsArray.map(Number) };
        } else {
          readings = resetsFilters.has('reading_level') ? { reading_level: [] } : {};
        }

        // Language codes: 3-letter strings ('eng'/'spa'), no coercion needed.
        let langs;
        if (languages?.length) {
          langs = { language: Array.isArray(languages) ? languages : [languages] };
        } else {
          langs = resetsFilters.has('language') ? { language: [] } : {};
        }

        const pageLoadParams = {
          searchParams: {
            ...query,
            page: Number(page) || pageDefault,
            per_page: Number(perPage) || perPageDefault,
            sort_field: sortBy || sortFieldDefault,
            sort_direction: sortDirection || sortDirectionDefault,
            ...assignees,
            ...projects,
            ...statuses,
            ...manuscripts,
            ...dateRange,
            ...types,
            ...grades,
            ...readings,
            ...langs,
          },
          ...search,
        };
        this.$patch(pageLoadParams);
      },

      [action.SET_SELECTED_MANUSCRIPT_IDS](manuscriptList) {
        this.selectedManuscriptIds = manuscriptList;
      },

      [action.UPDATE_USERS_ASSIGNED_A_COLOR]() {
        this.manuscripts.forEach((manuscript) => {
          if (!!manuscript.computed?.assignee?.id && !this.usersAssignedAColor[manuscript.computed?.assignee?.id]) {
            // Only set a random color if the user has not been cached yet.
            const assignee = manuscript.computed?.assignee;
            const hasName = !!assignee.first_name || !!assignee.last_name;
            const randomIndex = Math.floor(Math.random() * (USER_COLORS.length - 1));
            const backgroundColor = hasName ? (USER_COLORS[randomIndex] || USER_COLORS[0]) : nebulaTokens.color.platform.neutral['1000'];

            // Cache the color with the user ID as the key.
            this.usersAssignedAColor[assignee.id] = backgroundColor;
          }
        });
      },

      [action.CLEAR_MANUSCRIPT]() {
        this.manuscript = null;
      },

      async [action.DELETE_MANUSCRIPT](manuscriptId) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/${manuscriptId}`,
            {
              method: 'DELETE',
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          // Refresh the dashboard table so the deleted row disappears.
          await this[action.GET_MANUSCRIPTS]();

          // Remove the deleted id from selectedManuscriptIds to avoid stale selection state.
          this.selectedManuscriptIds = this.selectedManuscriptIds.filter(
            (id) => id !== manuscriptId,
          );

          return { success: true, data: response.data };
        } catch (error) {
          window.console.error('Manuscript Store: Failed to delete manuscript', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.GET_MANUSCRIPT](manuscriptId) {
        // Check cache first
        if (this.manuscript?.manuscript_id === manuscriptId) {
          window.console.log('📋 Manuscript: Using cached data for', manuscriptId);
          return this.manuscript;
        }

        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(`${URL_PREFIX}/manuscripts/${manuscriptId}`);
          this.manuscript = response.data;
          window.console.log('📋 Manuscript data received:', this.manuscript);
          return this.manuscript;
        } catch (error) {
          window.console.error('🏪 Manuscript Store: API call failed', error);
          this.manuscript = null;
          return null;
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.PATCH_MANUSCRIPT](manuscriptId, payload) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/${manuscriptId}`,
            {
              method: 'PATCH',
              body: payload,
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          // Update local manuscript state with updated manuscript
          this.manuscript = response.data;

          // Return the updated manuscript
          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error patching manuscript:', error);
          console.error('Error response:', error.response);
          console.error('Error data:', error.response?.data);
          console.error('Payload sent:', payload);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.POST_MANUSCRIPT](payload) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/`,
            {
              method: 'POST',
              body: payload,
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          // Update local manuscript state with created manuscript
          this.manuscript = response.data;

          // Refresh manuscripts list
          this[action.GET_MANUSCRIPTS]();

          // Return the created manuscript
          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error creating manuscript:', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.GET_MANUSCRIPTS]() {
        this.isProcessingRequest = true;
        let url = `${URL_PREFIX}/manuscripts/`;

        if (this.searchParamsOptions.string.length) {
          url += `?${this.searchParamsOptions.string}`;
        }

        try {
          const response = await this.appApi(url);
          const { data: { results, pagination } } = response;

          this.manuscripts = results;
          this.pagination = pagination;

          // Assign a color to each user and cache it the store.
          this.updateUsersAssignedAColor();

          return response.data;
        } catch (error) {
          console.error('Error fetching manuscripts:', error);
          return error;
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.GET_MANUSCRIPT_USERS]() {
        const url = `${URL_PREFIX}/users/`;
        try {
          const response = await this.appApi(url);
          const { data: { users } } = response;
          this.assigneesList = users;
          return response.data;
        } catch (error) {
          console.error('Error fetching manuscript users:', error);
          return error;
        }
      },

      async [action.GET_MANUSCRIPT_PRODUCT_CONFIG]() {
        const url = `${URL_PREFIX}/product_config/`;
        try {
          const response = await this.appApi(url);
          const { data } = response;
          // TODO - NGSSNATL-424 - Get status codes from product_config based on manuscripts product_id.
          this.statusCodes = data.product_configs[0].progress_statuses;
          this.productConfigs = data.product_configs;
          return data;
        } catch (error) {
          console.error('Error fetching manuscript: ', error);
          return error;
        }
      },

      async [action.POST_EXPORTS](payload) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/exports/`,
            {
              method: 'POST',
              body: payload,
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error posting manuscript: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.EXPORT_AS_STUDIO_RESOURCE]() {
        let bulkGenerateJob;

        try {
          if (!this.selectedManuscriptIds?.length) {
            throw new Error('At least one manuscript must be selected');
          }

          bulkGenerateJob = {
            manuscriptIds: this.selectedManuscriptIds,
            status: GENERATE_STATUS_GENERATING,
          };

          this.bulkGenerateLessonJobs.push(bulkGenerateJob);

          const response = await this.appApi(
            `${URL_PREFIX}/bulk-generate/`,
            {
              method: 'POST',
              body: {
                manuscript_ids: this.selectedManuscriptIds,
              },
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          bulkGenerateJob.bulkGenerationId = response.data.bulk_generation_id;

          this[action.POLL_BULK_GENERATION](bulkGenerateJob);

          return { success: true, data: response.data };
        } catch (error) {
          this[action.REMOVE_BULK_GENERATE_JOB](bulkGenerateJob?.bulkGenerationId);

          console.error('Error exporting manuscript: ', error);

          return {
            success: false,
            error: error.response?.data || error.message,
          };
        }
      },

      async [action.BULK_UPDATE_ASSIGNEE]({ manuscriptIds, assigneeId }) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/bulk-update`,
            {
              method: 'POST',
              body: {
                manuscript_ids: manuscriptIds,
                assignee_id: assigneeId,
              },
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          // Update the manuscripts in the store.
          await this[action.GET_MANUSCRIPTS]();

          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error bulk updating assignee: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.BULK_UPDATE_STATUS]({ manuscriptIds, statusId }) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/bulk-update`,
            {
              method: 'POST',
              body: {
                manuscript_ids: manuscriptIds,
                status_id: statusId,
              },
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          // Update the manuscripts in the store.
          await this[action.GET_MANUSCRIPTS]();

          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error bulk updating status: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      async [action.BULK_DELETE_MANUSCRIPTS](manuscriptIds) {
        this.isProcessingRequest = true;

        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/bulk-update`,
            {
              method: 'POST',
              // Sends is_deleted: true through the shared bulk-update endpoint. The
              // endpoint also supports is_deleted: false for restore; we do not surface
              // that path in NGSSNATL-1710 — see the spec under "OPEN QUESTIONS".
              body: {
                manuscript_ids: manuscriptIds,
                is_deleted: true,
              },
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          // Refresh the dashboard table so deleted rows disappear.
          await this[action.GET_MANUSCRIPTS]();

          // Clear the entire selection after a bulk delete — the deleted rows no longer exist.
          this.selectedManuscriptIds = [];

          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error bulk deleting manuscripts: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        } finally {
          this.isProcessingRequest = false;
        }
      },

      [action.POLL_BULK_GENERATION](job) {
        setTimeout(async () => {
          try {
            const { bulkGenerationId } = job;

            const response = await this.appApi(
              `${URL_PREFIX}/bulk-generate/${bulkGenerationId}`,
            );

            const { status } = response.data;

            const processingStatuses = [
              QUEUE_ITEM_STATUS_QUEUED,
              QUEUE_ITEM_STATUS_PROCESSING,
            ];

            if (processingStatuses.includes(status)) {
              this[action.POLL_BULK_GENERATION](job);
            } else {
              await this[action.UPDATE_STORE_MANUSCRIPTS_FROM_SERVER]({
                manuscriptIds: job.manuscriptIds,
                properties: ['metadata', 'generation_meta'],
              });

              this[action.REMOVE_BULK_GENERATE_JOB](bulkGenerationId);
            }
          } catch (error) {
            this[action.POLL_BULK_GENERATION](job);

            console.error('Error polling bulk generation job: ', error);
          }
        }, 10000);
      },

      [action.REMOVE_BULK_GENERATE_JOB](bulkGenerationId) {
        const jobIndex = this.bulkGenerateLessonJobs.findIndex(
          (item) => item.bulkGenerationId === bulkGenerationId,
        );

        if (jobIndex !== -1) {
          this.bulkGenerateLessonJobs.splice(jobIndex, 1);
        }
      },

      async [action.UPDATE_STORE_MANUSCRIPTS_FROM_SERVER]({
        manuscriptIds,
        properties,
      }) {
        const params = new URLSearchParams();

        forEach(manuscriptIds, (id) => {
          params.append('manuscript_id', id);
        });

        params.append('per_page', action.MAXIMUM_SELECTABLE_MANUSCRIPTS);

        const url = `${URL_PREFIX}/manuscripts/?${params.toString()}`;

        try {
          const response = await this.appApi(url);

          const { data: { results: manuscripts } } = response;

          forEach(manuscripts, (manuscript) => {
            this[action.UPDATE_STORE_MANUSCRIPT]({
              manuscript,
              properties,
            });
          });

          return response.data;
        } catch (error) {
          console.error('Error updating manuscripts from server:', error);
          return error;
        }
      },

      [action.UPDATE_STORE_MANUSCRIPT]({ manuscript, properties }) {
        if (!manuscript) return;

        const storeManuscript = this.manuscripts.find(
          (m) => m.manuscript_id === manuscript.manuscript_id,
        );

        if (!storeManuscript) return;

        forEach(properties, (property) => {
          storeManuscript[property] = manuscript[property];
        });
      },

      async [action.GENERATE_LESSON](manuscriptId) {
        try {
          this.generateLessonState[manuscriptId] = {
            status: GENERATE_STATUS_GENERATING,
          };

          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/${manuscriptId}/generate`,
            {
              method: 'POST',
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          const manuscript = response.data;

          this[action.UPDATE_STORE_MANUSCRIPT]({
            manuscript,
            properties: ['metadata', 'generation_meta'],
          });

          this.generateLessonState[manuscriptId] = null;

          return { success: true, data: response.data };
        } catch (error) {
          this.generateLessonState[manuscriptId] = {
            status: GENERATE_STATUS_ERROR,
          };

          await this[action.UPDATE_STORE_MANUSCRIPTS_FROM_SERVER]({
            manuscriptIds: [manuscriptId],
            properties: ['metadata', 'generation_meta'],
          });

          console.error('Error generating lesson: ', error);

          return {
            success: false,
            error: error.response?.data || error.message,
          };
        }
      },

      async [action.FETCH_DRAFT_FOR_PREVIEW](draftId) {
        try {
          const response = await this.titanApiFetch(`/drafts/${draftId}?reference_type=asset`);
          return { success: true, data: response.data.draft };
        } catch (error) {
          console.error('Error fetching creation for preview: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        }
      },

      async [action.GENERATE_PAGE](manuscriptId, pageId) {
        try {
          const response = await this.appApi(
            `${URL_PREFIX}/manuscripts/${manuscriptId}/pages/${pageId}/generate`,
            {
              method: 'POST',
              headers: {
                'X-Token': useUserStore().user.token,
              },
            },
          );

          return { success: true, data: response.data };
        } catch (error) {
          console.error('Error generating page: ', error);
          return {
            success: false,
            error: error.response?.data || error.message,
          };
        }
      },

    },
  },
);

export default useManuscriptStore;
